import BasicWorkspace from "./common/BasicWorkspace";
import {Scene} from "../scene/types";
import {ToggleDefs} from "../scene/rowVisibility";

export interface Design {
    width: number;
    height: number;
    html?: string;            // legacy 'html' engine
    engine?: 'html' | 'scene';
    scene?: Scene;            // canvas 'scene' engine
    /** The RAW Figma-derived label. Kept alongside displayName so the original
     *  stays inspectable — and so a surface can tell whether an admin actually
     *  renamed this design (see hasDesignNameOverride). */
    name: string;
    /**
     * What a tab should SHOW: the admin's override when one exists, otherwise
     * `name`. RESOLVED SERVER-SIDE so the fallback rule lives in one place;
     * optional here only because older cached payloads predate the field.
     */
    displayName?: string;
    /** The immutable identifier. Renaming never touches it, so scene lookups,
     *  drafts, share links and generation records can never be orphaned. */
    key: string;
}

/** What to render for a design, anywhere a human reads it. `key` is the last
 *  resort: a design with neither label is malformed, but a blank tab is worse
 *  than an ugly one. */
export const designLabel = (design?: Pick<Design, 'name' | 'displayName' | 'key'> | null): string =>
    design?.displayName || design?.name || design?.key || '';

/** Whether an admin explicitly renamed this design. The end-user payload
 *  resolves displayName server-side, so an override is only detectable as a
 *  DIVERGENCE from the raw name — which is what tells a surface to show the
 *  value verbatim instead of applying its own labelling heuristic. */
export const hasDesignNameOverride = (design?: Pick<Design, 'name' | 'displayName'> | null): boolean =>
    !!design?.displayName && design.displayName !== design.name;

/** What to render for a row (a `<name>#group` in Figma). The row KEY stays the
 *  identifier everywhere — input keys, drafts, toggle keys — so this is display only. */
export const rowLabel = (template: Pick<BaseTemplate, 'rowDisplayNames'> | null | undefined, key: string): string =>
    rowDisplayName(template, key) || key;

/** The admin's stored label for a row, if any. An OWN-property read: row keys
 *  are Figma names, and a row called `constructor` must not find Object's. */
export const rowDisplayName = (template: Pick<BaseTemplate, 'rowDisplayNames'> | null | undefined,
                               key: string): string | undefined => {
    const names = template?.rowDisplayNames;
    return names && Object.prototype.hasOwnProperty.call(names, key) ? names[key] : undefined;
};

export interface Font {
    id: number;
    urls: string[];
}

interface BaseTemplate {
    id: string;
    inputs: {
        [key: string]: ImageInput | TextInput;
    };
    designs: Design[]
    name: string;
    fonts: {
        [key: string]: Font
    }
    thumbnailUrl: string;
    // Fill Mode: when false, editing a text field from a checklist CARD does not
    // scroll the board to the edited element. Missing/true = scroll (default).
    fillModeScrollOnCardEdit?: boolean;
    // When true the end user may add their own text/image layers on top of the
    // template and reorder them. Missing/false = off — a template has to be
    // opted in by an admin.
    userLayersEnabled?: boolean;
    // When true the template is offered in the admin's "attach WhatsApp
    // templates to a user" list. Missing/false = not a WhatsApp template.
    whatsappEnabled?: boolean;
    // The admin-chosen order the end user fills fields in, as input keys.
    // ABSENT means never set — every surface then keeps its own legacy ordering,
    // so this must stay optional rather than defaulting to [].
    fillOrder?: string[] | null;
    // Layers the admin let the end user switch off, keyed by Figma node id.
    // Absent = none. Resolution (incl. per-row fan-out) is scene/rowVisibility.
    toggles?: ToggleDefs;
    // Admin labels for the rows, keyed by row key. Absent key = the row key
    // itself is the label (see rowLabel).
    rowDisplayNames?: Record<string, string>;
    // Bumped ONLY on a design change (Figma re-sync). Drafts saved against an
    // older revision surface a "template updated" notice on restore. Optional:
    // pre-Phase-0 templates predate the field.
    contentRevision?: number;
    /**
     * The RAW override map, keyed by designKey — ADMIN responses only.
     *
     * Kept alongside each design's resolved `displayName` because the two
     * answer different questions: the resolved name says what to render, this
     * says whether an admin actually typed it. A rename field seeded from the
     * resolved name would mint an override on the next save.
     */
    designDisplayNames?: { [designKey: string]: string };
    createdAt: string;
    updatedAt?: string;
}

/** Per-field data the admin canvas needs but the end user never sees: which
 *  fields carry stored edits, and the FIGMA BASELINE the reset action applies.
 *  Derived server-side from the stored delta maps — the maps themselves (whose
 *  keys are Mongo-encoded) never leave the API. */
export interface DesignEditState {
    geometryEditedInputKeys: string[];
    textEditedInputKeys: string[];
    editedFields: {
        [inputKey: string]: {
            /** elementId -> the element's Figma-original absolute box. */
            elements: { [elementId: string]: { x: number; y: number; w: number; h: number } };
            /** The Figma-original input default, for a text field. */
            originalDefault?: string;
        };
    };
}

export interface AdminTemplate extends BaseTemplate {
    workspaces: BasicWorkspace[];
    /** Present once the admin design-edit endpoints exist server-side; absent on
     *  an older API, which simply means "no field has been edited". */
    designEditState?: DesignEditState;
    // Lineage back to the TemplateImport this template was converted from. Null/
    // absent until an existing template is linked on demand (re-sync Phase B).
    sourceImportId?: string;
    // The admin fetch additionally exposes the hidden AI-edit config per image
    // input (see AdminImageInput). Consumers narrow an entry to AdminImageInput
    // when they need those fields — the base `inputs` map stays public-typed so
    // this override is compatible with BaseTemplate.
}

export default interface Template extends BaseTemplate {
    workspace: BasicWorkspace;
}

/** Response of GET /admin/templates/{id}/resync/preview — the reconciliation of
 *  old→new inputs after a Figma re-pull. `ready` is false while the candidate is
 *  still being computed; once ready, the four buckets describe how the current
 *  template's inputs map onto the freshly re-imported design:
 *   - matched:   auto-resolved by stable Figma node id (rename = oldKey!==newKey).
 *   - added:     new inputs with no old counterpart.
 *   - removed:   old inputs with no new counterpart (dropped unless remapped).
 *   - ambiguous: old inputs whose match is uncertain — admin must confirm. */
export interface ResyncPreview {
    ready: boolean;
    status?: string;
    contentRevision?: number;
    // Optimistic-concurrency tokens echoed back on apply so the server can bind
    // the apply to exactly the preview the admin reviewed: `updatedAt` guards the
    // template config, `candidateToken` guards the Figma-pull generation. A 409
    // means one of them moved (template changed OR the pull was re-run).
    updatedAt?: string;
    candidateToken?: string;
    matched?: { oldKey: string; newKey: string }[];
    added?: string[];
    removed?: string[];
    ambiguous?: { oldKey: string; suggestions: string[] }[];
}

export interface BaseTemplateListItem {
    id: string;
    designs: string[];
    name: string;
    thumbnailUrl: string;
    fillModeScrollOnCardEdit?: boolean;
    userLayersEnabled?: boolean;
    whatsappEnabled?: boolean;
    createdAt: string;
    updatedAt?: string;
}

export interface AdminTemplateListItem extends BaseTemplateListItem {
    workspaces: BasicWorkspace[];
}

export interface TemplateListItem extends BaseTemplateListItem {
    workspace: BasicWorkspace;
}

export interface ImageValue {
    /** The asset this value came from. The API stamps it on every image input
     *  DEFAULT (`ImageInputDefault.assetId`, template_model.py); client-side
     *  paths that build an ImageValue from an Asset copy the Asset's `id` into
     *  it. Optional because legacy defaults, Excel rows and older drafts have
     *  none — identity comparison (scene/imageFit.ts) falls back to the URL. */
    assetId?: string,
    name: string,
    highRes: string,
    thumbnail: string
    width?: number;
    height?: number;
    type?: string;
    duration?: number;  // Video duration in seconds
}

interface BaseInput {
    type: 'image' | 'text';
    placeholder: string;
    default: ImageValue | string;
    editable: boolean;
    maxChars?: number;
}

interface Size {
    width: number;
    height: number;
}

/** A named prompt option the user picks between before running AI. The PUBLIC
 *  DTO carries names only — the prompt text is never sent to the client. */
export interface AiPromptVariant {
    id: string;
    name: string;
}

/** The admin variant additionally carries the (hidden) prompt text. `id` is
 *  optional on save — the backend assigns one for newly-added rows. */
export interface AdminAiPromptVariant {
    id?: string;
    name: string;
    prompt: string;
}

export interface ImageInput extends BaseInput {
    type: 'image';
    placeholder: string;
    default: ImageValue;
    editable: boolean;
    size: {
        [key: string]: Size
    }
    folderId?: string;
    ref: string;
    videoEnabled?: boolean;
    // Public flag only: after a fresh upload into this field the user is offered
    // the "AI magic" stage. The prompt itself lives server-side (admin only).
    aiEditable?: boolean;
    // When true, the AI result is a transparent cutout the backend pads to the
    // field shape, so it drops in showing the whole subject — the AI-result crop
    // step is then skipped. Populated on the public DTO by the server; while it's
    // undefined the crop step defaults ON (safe: a full-scene result could clip).
    aiRemoveBackground?: boolean;
    // Named style options the user picks between (names only — no prompt text).
    aiPromptVariants?: AiPromptVariant[];
}

/** Image input as returned by the ADMIN template fetch — same shape plus the
 *  hidden AI-edit configuration. The variant list carries prompt text here
 *  (admin only), so it overrides the public names-only field. */
export interface AdminImageInput extends Omit<ImageInput, 'aiPromptVariants'> {
    aiRemoveBackground?: boolean;
    aiPromptVariants?: AdminAiPromptVariant[];
}

export interface TextInput extends BaseInput {
    type: 'text';
    placeholder: string;
    default: string;
    editable: boolean;
    inputType: string;
    maxChars: number;
}